import React, { Fragment } from 'react';
import {
  I18n,
  FormForm,
  SubmissionSearch,
  searchSubmissions,
  bundle,
} from '@kineticdata/react';
import { compose, withHandlers } from 'recompose';
import { connect } from '../../redux/store';
import {
  FormComponents,
  LoadingMessage,
  Utils,
  addToast,
} from '@kineticdata/bundle-common';
import {
  actions,
  buildFormConfigurationObject,
} from '../../redux/modules/adminForms';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PageTitle } from '../shared/PageTitle';

export const FieldsTableField = props => (
  <table className="table table-hover table--settings table-draggable">
    <thead>
      <tr className="header">
        <th scope="col">
          <I18n>Field</I18n>
        </th>
        <th scope="col">
          <I18n>Visible</I18n>
        </th>
        <th scope="col">
          <I18n>Filterable</I18n>
        </th>
        <th scope="col">
          <I18n>Sortable</I18n>
        </th>
      </tr>
    </thead>
    {props.value && (
      <DragDropContext
        onDragEnd={({ source, destination }) =>
          destination &&
          source.index !== destination.index &&
          props.onChange(
            props.value.update(cols => {
              const col = cols.get(source.index);
              return cols.delete(source.index).insert(destination.index, col);
            }),
          )
        }
      >
        <Droppable droppableId="columns">
          {provided => (
            <tbody ref={provided.innerRef}>
              {props.value.map((col, index) => (
                <Draggable key={col.name} draggableId={col.name} index={index}>
                  {(provided, snapshot) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`${snapshot.isDragging ? 'dragging' : ''}`}
                    >
                      <td>
                        {col.type === 'value' ? (
                          <I18n>{col.label}</I18n>
                        ) : (
                          <i>
                            <I18n>{col.label}</I18n>{' '}
                            <small>
                              <I18n>(system field)</I18n>
                            </small>
                          </i>
                        )}
                      </td>
                      <td>
                        <input
                          onChange={e =>
                            props.onChange(
                              props.value.setIn(
                                [index, 'visible'],
                                e.target.checked,
                              ),
                            )
                          }
                          type="checkbox"
                          checked={col.visible}
                        />
                      </td>
                      <td>
                        <input
                          onChange={e =>
                            props.onChange(
                              props.value.setIn(
                                [index, 'filterable'],
                                e.target.checked,
                              ),
                            )
                          }
                          type="checkbox"
                          checked={col.filterable}
                        />
                      </td>
                      <td>
                        <input
                          onChange={e =>
                            props.onChange(
                              props.value.setIn(
                                [index, 'sortable'],
                                e.target.checked,
                              ),
                            )
                          }
                          type="checkbox"
                          checked={col.sortable}
                        />
                      </td>
                    </tr>
                  )}
                </Draggable>
              ))}
            </tbody>
          )}
        </Droppable>
      </DragDropContext>
    )}
    {props.helpText && (
      <tfoot>
        <tr>
          <td colSpan="4">
            <small>{props.helpText}</small>
          </td>
        </tr>
      </tfoot>
    )}
  </table>
);

const fieldSet = [
  'name',
  'slug',
  'status',
  'type',
  'submissionLabelExpression',
  'description',
  'attributesMap',
  'submissionTableFields',
];

const FormLayout = ({ fields, error, buttons }) => (
  <Fragment>
    <h2 className="section__title">
      <I18n>General</I18n>
    </h2>
    <div className="form-group__columns">
      {fields.get('name')}
      {fields.get('slug')}
    </div>
    <div className="form-group__columns">
      {fields.get('status')}
      {fields.get('type')}
    </div>
    {fields.get('submissionLabelExpression')}
    {fields.get('description')}
    <br />
    <h2 className="section__title">
      <I18n>Submission Table - Default Columns</I18n>
    </h2>
    {fields.get('submissionTableFields')}
    {error}
    {buttons}
  </Fragment>
);

const notificationSearch = new SubmissionSearch(true)
  .index('values[Name]')
  .includes(['values'])
  .limit(1000)
  .build();

export const FormSettingsComponent = ({ form, onSave, taskSourceName }) => {
  return (
    <FormForm
      kappSlug="admin"
      formSlug={form.slug}
      fieldSet={fieldSet}
      onSave={onSave}
      components={{ FormLayout }}
      addDataSources={{
        notifications: {
          fn: searchSubmissions,
          params: [
            {
              datastore: true,
              form: 'notification-data',
              search: notificationSearch,
            },
          ],
          transform: result => result.submissions,
        },
      }}
      addFields={() => ({ form, notifications }) =>
        form && [
          {
            name: 'submissionTableFields',
            label: 'Submission Table - Fields',
            type: 'custom',
            helpText:
              'Select which field columns should be visible by default when displaying submissions for this form in the settings pages. Drag and drop to change the order in which the columns will appear.',
            initialValue: buildFormConfigurationObject(form.toJS()).columns,
            component: FieldsTableField,
          },
        ]}
      alterFields={{
        description: { component: FormComponents.TextAreaField },
        attributesMap: {
          serialize: ({ values }) => ({
            'Datastore Configuration': [
              // TODO Update to allow for other props in Form Config attribute
              JSON.stringify({
                columns: values.get('submissionTableFields').toJS(),
              }),
            ],
          }),
        },
      }}
    >
      {({ form: formContent, initialized }) => (
        <div className="page-container">
          <div className="page-panel">
            <PageTitle
              parts={['Settings', form.name, `Admin Forms`]}
              breadcrumbs={[
                { label: 'Admin Forms', to: '../..' },
                { label: form.name, to: '..' },
              ]}
              title={
                <span>
                  <I18n>Settings</I18n>{' '}
                  <small>
                    <small>
                      <I18n>for</I18n>
                    </small>{' '}
                    <I18n>{form.name}</I18n>
                  </small>
                </span>
              }
              actions={[
                {
                  icon: 'mouse-pointer',
                  label: 'Form Builder',
                  href: `${bundle.spaceLocation()}/app/console/#/kapps/admin/forms/edit/${
                    form.slug
                  }/builder`,
                },
              ]}
            />

            {initialized ? (
              <section className="form form-unstyled mb-5">
                {formContent}
              </section>
            ) : (
              <LoadingMessage />
            )}
          </div>
        </div>
      )}
    </FormForm>
  );
};

const mapStateToProps = state => ({
  taskSourceName: Utils.getAttributeValue(state.app.space, 'Task Source Name'),
});

const mapDispatchToProps = { fetchFormRequest: actions.fetchFormRequest };

export const FormSettings = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    onSave: props => () => () => {
      props.fetchFormRequest({
        kappSlug: 'admin',
        formSlug: props.form.slug,
      });
      addToast(`${props.form.name} settings saved successfully.`);
    },
  }),
)(FormSettingsComponent);
